import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table } from 'reactstrap';
import { parseAndFormatQuestionResponse } from 'utils/models/Performance';
import {
  extractSurveyAnswerResponse,
  noAnswerElement,
} from 'views/CustomSurveys/formatSurveyAnswer';
import EmptyState from 'views/Widgets/EmptyState';
import { type SurveyResponsesData } from '../consts';

interface Props {
  data: SurveyResponsesData;
}

// Tabular alternative to a distribution chart, for free-text (or any other)
// questions with no fixed value domain to bucket. Anonymized - no
// respondent is ever attached, and content-based ordering (done server-side)
// carries no positional identity signal either.
const SurveyResponsesTable: FC<Props> = ({ data }) => {
  const { formatMessage, locale } = useIntl();

  if (data.suppressed) {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
          defaultMessage: 'Not enough responses to display anonymously.',
        })}
      />
    );
  }

  if (data.responses.length === 0) {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.administration.custom_surveys.charts.responses_table.empty',
          defaultMessage: 'No responses yet for this question.',
        })}
      />
    );
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>
              <FormattedMessage
                id="app.views.administration.custom_surveys.charts.responses_table.col.response"
                defaultMessage="Response"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.responses.map((entry, index) => (
            <tr key={index}>
              <td>
                {parseAndFormatQuestionResponse({
                  q: {
                    question: data.question.definition,
                    response: extractSurveyAnswerResponse(
                      data.question.type,
                      entry.answer
                    ),
                  },
                  noneElement: noAnswerElement,
                  formatMessage,
                  locale,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="text-muted fs-3 text-center">
        <FormattedMessage
          id="app.views.administration.custom_surveys.charts.responses_table.count"
          defaultMessage="{count, plural, one {# response} other {# responses}}"
          values={{ count: data.response_count ?? data.responses.length }}
        />
      </div>
    </>
  );
};

export default SurveyResponsesTable;
